import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

function useQueryState<T extends string>(
    key: string,
    defaultValue?: T
): [T, Dispatch<SetStateAction<T>>] {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const getParam = useCallback((): T => (searchParams.get(key) as T) ?? defaultValue, [defaultValue, key, searchParams]);

    const [value, setValue] = useState<T>(getParam);

    const update: Dispatch<SetStateAction<T>> = useCallback((newValue) => {
        const params = new URLSearchParams(window.location.search);
        const currentValue = (params.get(key) as T) ?? defaultValue;

        const resolved =
            typeof newValue === "function"
                ? (newValue as (prev: T) => T)(currentValue)
                : newValue;

        resolved
            ? params.set(key, encodeURIComponent(resolved))
            : params.delete(key);

        setSearchParams(params, { replace: true });
        setValue(resolved);
    }, [key, defaultValue, setSearchParams]);

    // keep the state in sync with URL (on mount + back/forward)
    useEffect(() => {
        setValue(getParam());
    }, [location.search, getParam]);

    return [value, update];
}

export default useQueryState;
